<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useEChart } from '@/composables/useEChart'
import { useViewport } from '@/composables/useViewport'
import { stockDailyApi, stockBasicApi } from '@/api/stock'
import { toast } from '@/composables/useToast'
import PageHero from '@/components/common/PageHero.vue'

const LOOKBACK = 30 // 图表显示的K线根数
const MIN_FUTURE = 30 // 最后一个显示日期之后必须保留的交易日数
const MAX_LOTS = 1000 // 默认可买手数

const { chartRef, init } = useEChart()
const { isMobile } = useViewport()

const loading = ref(false)
const pool = ref([])
const poolLoaded = ref(false)

const symbol = ref('')
const daily = ref([]) // 全部日线（升序）
const cutIndex = ref(-1) // 当前窗口最后一天在 daily 中的下标
const windowStart = ref(0) // 窗口起点下标（下一天后固定，K线逐日追加）
const lots = ref(0) // 已买入手数
const avgCost = ref(0) // 持仓成本（每股）
const realizedPnl = ref(0) // 已实现盈亏
const totalInvested = ref(0) // 累计投入本金（每次买入金额累加）
const qty = ref(1000) // 单次买卖手数

const currentBar = computed(() => daily.value[cutIndex.value] || null)
const currentClose = computed(() => currentBar.value?.close != null ? Number(currentBar.value.close) : null)
const remaining = computed(() => Math.max(0, MAX_LOTS - lots.value))
const floatPnl = computed(() => {
  if (lots.value <= 0 || !avgCost.value || currentClose.value == null) return 0
  return (currentClose.value - avgCost.value) * lots.value * 100
})
const historyPct = computed(() => {
  if (totalInvested.value <= 0) return 0
  const totalPnl = floatPnl.value + realizedPnl.value
  return (totalPnl / totalInvested.value) * 100
})
const canNext = computed(() => cutIndex.value >= 0 && cutIndex.value < daily.value.length - 1)
const windowData = computed(() => {
  if (cutIndex.value < 0) return []
  return daily.value.slice(windowStart.value, cutIndex.value + 1)
})
const windowDays = computed(() => windowData.value.length)
const futureDays = computed(() => Math.max(0, daily.value.length - 1 - cutIndex.value))

function getThemeColor (name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '#999'
}

function fmt (v) {
  if (v == null) return '-'
  const n = Number(v)
  if (Number.isNaN(n)) return '-'
  return n.toFixed(2)
}

function pnlClass (v) {
  const n = Number(v)
  if (n > 0) return 'up'
  if (n < 0) return 'down'
  return ''
}

// ==================== 股票池与随机选股 ====================

async function ensurePool () {
  if (poolLoaded.value) return pool.value
  try {
    const res = await stockBasicApi.pageQuery({ page_size: 500, sort_field: 'symbol', sort_order: 'asc' })
    pool.value = (res?.list || []).filter(s => s.symbol)
    poolLoaded.value = true
  } catch (e) {
    console.error('加载股票池失败:', e)
    pool.value = []
  }
  return pool.value
}

function pickRandomStock () {
  if (!pool.value.length) return null
  return pool.value[Math.floor(Math.random() * pool.value.length)]
}

async function loadStock (stock) {
  try {
    const data = await stockDailyApi.getBySymbol(stock.symbol)
    if (!Array.isArray(data) || data.length === 0) return null
    const list = data
      .filter(i => i.close != null && i.open != null && i.high != null && i.low != null)
      .sort((a, b) => a.trade_date.localeCompare(b.trade_date))
    if (list.length < LOOKBACK + MIN_FUTURE + 1) return null
    return list
  } catch (e) {
    console.error(`加载 ${stock.symbol} 日线失败:`, e)
    return null
  }
}

function resetPosition () {
  lots.value = 0
  avgCost.value = 0
  realizedPnl.value = 0
  totalInvested.value = 0
}

async function switchStock () {
  loading.value = true
  try {
    await ensurePool()
    if (!pool.value.length) {
      toast.error('股票池为空，无法训练')
      return
    }
    let list = null
    let stock = null
    // 最多尝试 20 次，找到满足 30 日显示 + 30 日余量的股票
    for (let i = 0; i < 20; i++) {
      stock = pickRandomStock()
      if (!stock) break
      list = await loadStock(stock)
      if (list) break
    }
    if (!list || !stock) {
      toast.warning('暂未找到满足训练条件（历史长度足够）的股票，请重试')
      return
    }
    symbol.value = stock.symbol
    daily.value = list
    // 最后一个显示日期之后必须还有至少 30 个交易日
    const maxCut = list.length - 1 - MIN_FUTURE
    const minCut = LOOKBACK - 1
    cutIndex.value = minCut + Math.floor(Math.random() * (maxCut - minCut + 1))
    // 窗口起点固定为初始 30 天窗口的首日，下一天后 K 线逐日追加
    windowStart.value = cutIndex.value - LOOKBACK + 1
    resetPosition()
    toast.success(`已切换到 ${stock.symbol}（剩余 ${futureDays.value} 个交易日可训练）`)
    renderChart()
  } catch (e) {
    toast.error('切换股票失败: ' + (e.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

// ==================== 训练操作 ====================

function onBuy () {
  if (!currentBar.value) return
  const price = currentClose.value
  if (price == null) {
    toast.warning('当前日期无有效价格')
    return
  }
  const q = Math.min(qty.value, remaining.value)
  if (q <= 0) {
    toast.warning('已达可买上限 1000 手')
    return
  }
  const totalCost = avgCost.value * lots.value + price * q
  lots.value += q
  avgCost.value = lots.value > 0 ? totalCost / lots.value : 0
  totalInvested.value += price * q * 100
  toast.success(`买入 ${q} 手 @ ${price.toFixed(2)}`)
}

function onSell () {
  if (lots.value <= 0) {
    toast.warning('当前没有持仓')
    return
  }
  const price = currentClose.value
  if (price == null) {
    toast.warning('当前日期无有效价格')
    return
  }
  const q = Math.min(qty.value, lots.value)
  realizedPnl.value += (price - avgCost.value) * q * 100
  lots.value -= q
  if (lots.value === 0) avgCost.value = 0
  toast.success(`卖出 ${q} 手 @ ${price.toFixed(2)}`)
}

function onNext () {
  if (!canNext.value) {
    toast.info('已到最新交易日，请切换股票继续训练')
    return
  }
  cutIndex.value += 1
  renderChart()
}
// ==================== 图表 ====================

function renderChart () {
  nextTick(() => {
    if (!chartRef.value) return
    const list = windowData.value
    if (!list.length) return

    const dates = list.map(i => i.trade_date)
    const kline = list.map(i => [Number(i.open), Number(i.close), Number(i.low), Number(i.high)])
    const volumes = list.map(i => ({
      value: Number(i.volume),
      itemStyle: {
        color: Number(i.close) >= Number(i.open) ? getThemeColor('--up') : getThemeColor('--down')
      }
    }))

    const upColor = getThemeColor('--up')
    const downColor = getThemeColor('--down')
    const axisColor = getThemeColor('--text-muted')
    const textColor = getThemeColor('--text-faint')
    const tooltipBg = getComputedStyle(document.documentElement).getPropertyValue('--bg-secondary').trim() || 'rgba(17,17,17,0.95)'
    const tooltipBorder = getThemeColor('--rule')
    const tooltipText = getThemeColor('--text-primary')

    function fmtVol (v) {
      const n = Number(v)
      if (!n) return '-'
      if (n >= 1e8) return (n / 1e8).toFixed(2) + '亿'
      if (n >= 1e4) return (n / 1e4).toFixed(2) + '万'
      return n.toFixed(0)
    }

    init({
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: tooltipBg,
        borderColor: tooltipBorder,
        textStyle: {
          color: tooltipText,
          fontFamily: 'Consolas, Menlo, monospace',
          fontSize: 11
        },
        formatter (params) {
          const p = params?.[0]
          if (!p || p.dataIndex == null) return ''
          const item = list[p.dataIndex]
          if (!item) return ''
          const chg = item.pct_chg != null ? Number(item.pct_chg) : null
          const chgStr = chg == null ? '-' : (chg > 0 ? '+' : '') + chg.toFixed(2) + '%'
          const chgColor = chg == null ? '' : (chg >= 0 ? 'color:#ff4d4f' : 'color:#52c41a')
          return `<div style="font-family:Consolas,Menlo,monospace;font-size:11px;line-height:1.7;">
            <div style="font-weight:600;margin-bottom:4px;">${item.trade_date}</div>
            <div>OPEN&nbsp;&nbsp;${Number(item.open).toFixed(2)}</div>
            <div>CLOSE&nbsp;${Number(item.close).toFixed(2)}</div>
            <div>HIGH&nbsp;&nbsp;${Number(item.high).toFixed(2)}</div>
            <div>LOW&nbsp;&nbsp;&nbsp;${Number(item.low).toFixed(2)}</div>
            <div>CHG&nbsp;&nbsp;&nbsp;<span style="${chgColor};font-weight:600">${chgStr}</span></div>
            <div>VOL&nbsp;&nbsp;&nbsp;${fmtVol(item.volume)}</div>
          </div>`
        }
      },
      axisPointer: {
        link: [{ xAxisIndex: 'all' }]
      },
      grid: [
        {
          left: 8,
          right: 60,
          top: isMobile.value ? 12 : 18,
          height: isMobile.value ? '56%' : '58%'
        },
        {
          left: 8,
          right: 60,
          top: isMobile.value ? '72%' : '74%',
          height: isMobile.value ? '16%' : '14%'
        }
      ],
      xAxis: [
        {
          type: 'category',
          data: dates,
          scale: true,
          boundaryGap: false,
          axisLine: { onZero: false, lineStyle: { color: axisColor } },
          axisLabel: { color: textColor, fontFamily: 'Consolas, Menlo, monospace', fontSize: 10 },
          splitLine: { show: false },
          min: 'dataMin',
          max: 'dataMax'
        },
        {
          type: 'category',
          gridIndex: 1,
          data: dates,
          scale: true,
          boundaryGap: true,
          axisLine: { onZero: false, lineStyle: { color: axisColor } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          min: 'dataMin',
          max: 'dataMax'
        }
      ],
      yAxis: [
        {
          type: 'value',
          scale: true,
          position: 'right',
          axisLine: { lineStyle: { color: axisColor } },
          axisLabel: { color: textColor, fontFamily: 'Consolas, Menlo, monospace', fontSize: 10 },
          splitLine: { lineStyle: { color: getThemeColor('--rule'), opacity: 0.6 } }
        },
        {
          type: 'value',
          scale: true,
          gridIndex: 1,
          splitNumber: 2,
          position: 'right',
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false }
        }
      ],
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0, 1],
          start: 0,
          end: 100
        },
        {
          show: true,
          xAxisIndex: [0, 1],
          type: 'slider',
          top: isMobile.value ? '90%' : '92%',
          start: 0,
          end: 100,
          height: isMobile.value ? 28 : 18,
          borderColor: 'transparent',
          fillerColor: 'rgba(59, 130, 246, 0.12)',
          handleStyle: { color: '#3b82f6' },
          textStyle: {
            color: textColor,
            fontFamily: 'Consolas, Menlo, monospace',
            fontSize: 9
          },
          dataBackground: {
            lineStyle: { color: axisColor, opacity: 0.3 },
            areaStyle: { color: axisColor, opacity: 0.15 }
          },
          selectedDataBackground: {
            lineStyle: { color: '#3b82f6' },
            areaStyle: { color: 'rgba(59, 130, 246, 0.2)' }
          }
        }
      ],
      series: [
        {
          name: '日K',
          type: 'candlestick',
          data: kline,
          itemStyle: {
            color: upColor,
            color0: downColor,
            borderColor: upColor,
            borderColor0: downColor
          },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: getThemeColor('--accent'), type: 'dashed', width: 1 },
            label: {
              show: true,
              position: 'start',
              formatter: '当前',
              color: getThemeColor('--accent'),
              fontFamily: 'Consolas, Menlo, monospace',
              fontSize: 10
            },
            data: [{ xAxis: dates.length - 1 }]
          }
        },
        {
          name: '成交量',
          type: 'bar',
          gridIndex: 1,
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: volumes,
          barWidth: '60%'
        }
      ]
    })
  })
}

onMounted(() => {
  switchStock()
})
</script>

<template>
  <div class="kline-train page">
    <PageHero>
      <template #actions>
        <div class="form-row inline">
          <label>手数<input v-model.number="qty" type="number" min="1" step="100"></label>
          <button class="btn-buy" :disabled="loading" @click="onBuy">买入</button>
          <button class="btn-sell" :disabled="loading" @click="onSell">卖出</button>
          <button class="btn-config" :disabled="loading" @click="switchStock">切换股票</button>
          <button class="btn-primary" :disabled="loading || !canNext" @click="onNext">
            下一天
          </button>
        </div>
      </template>
    </PageHero>

    <div v-if="daily.length" class="train-info">
      <span class="info-code">{{ symbol }}</span>
      <span class="info-range">
        {{ windowData[0]?.trade_date }} ~ {{ currentBar?.trade_date }}
      </span>
      <span class="info-days">已显示 {{ windowDays }} 日</span>
      <span class="info-future">剩余 {{ futureDays }} 个交易日</span>
    </div>

    <div class="chart-frame">
      <div ref="chartRef" class="train-chart"></div>
      <div v-if="loading" class="chart-state">加载中…</div>
      <div v-else-if="!daily.length" class="chart-state">暂无数据</div>
    </div>

    <div v-if="daily.length" class="status-grid">
      <div class="stat-card">
        <div class="stat-label">默认可买</div>
        <div class="stat-value">{{ MAX_LOTS }} 手</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">已买入</div>
        <div class="stat-value">{{ lots }} 手</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">剩余</div>
        <div class="stat-value">{{ remaining }} 手</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">历史涨幅</div>
        <div class="stat-value" :class="pnlClass(historyPct)">
          {{ historyPct > 0 ? '+' : '' }}{{ historyPct.toFixed(2) }}%
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-label">现价 / 成本</div>
        <div class="stat-value small">{{ fmt(currentClose) }} / {{ fmt(avgCost) }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">浮动盈亏</div>
        <div class="stat-value small" :class="pnlClass(floatPnl)">
          {{ floatPnl > 0 ? '+' : '' }}{{ floatPnl.toFixed(0) }}
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-label">已实现盈亏</div>
        <div class="stat-value small" :class="pnlClass(realizedPnl)">
          {{ realizedPnl > 0 ? '+' : '' }}{{ realizedPnl.toFixed(0) }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.kline-train {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 4px 28px 18px;
  box-sizing: border-box;
  overflow: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100%;
    overflow: visible;
    padding: 4px 16px 12px;
  }
}

.form-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  &.inline {
    label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: var(--text-secondary);
    }
  }

  input {
    width: 90px;
    padding: 6px 8px;
    background: var(--bg-input);
    color: var(--text-primary);
    border: 1px solid var(--rule);
    border-radius: 6px;
    font-size: 13px;
    outline: none;

    &:focus {
      border-color: var(--border-focus);
    }
  }
}

.btn-config {
  padding: 7px 14px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: var(--text-secondary);
  background: transparent;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }
}

.btn-buy {
  padding: 7px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #fff;
  background: var(--up);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: var(--up-bg-hover);
    color: var(--up);
  }
}

.btn-sell {
  padding: 7px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #fff;
  background: var(--down);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: var(--down-bg-hover);
    color: var(--down);
  }
}

.btn-primary {
  padding: 7px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #fff;
  background: var(--accent);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: var(--accent-hover);
  }
}

.train-info {
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 10px 0 8px;
  flex-wrap: wrap;

  .info-code {
    font-family: var(--font-mono);
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: 0.06em;
  }

  .info-range {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
  }

  .info-days {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
  }

  .info-future {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--accent);
  }
}

.chart-frame {
  position: relative;
  background: var(--bg-secondary);
  border: 1px solid var(--rule);
  border-radius: 8px;
  overflow: hidden;
  flex: 1;
  min-height: 360px;

  @media (max-width: 768px) {
    min-height: 300px;
  }
}

.train-chart {
  width: 100%;
  height: 100%;
  min-height: 360px;

  @media (max-width: 768px) {
    min-height: 300px;
  }
}

.chart-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 13px;
  color: var(--text-faint);
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
}

.stat-card {
  background: var(--bg-secondary);
  border: 1px solid var(--rule);
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-faint);
}

.stat-value {
  font-family: var(--font-mono);
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.2;

  &.small {
    font-size: 15px;
  }

  &.up {
    color: var(--up);
  }

  &.down {
    color: var(--down);
  }
}

@media (max-width: 420px) {
  .stat-card {
    padding: 8px 10px;
  }

  .stat-value {
    font-size: 16px;

    &.small {
      font-size: 13px;
    }
  }
}
</style>
